import { useEffect, useRef, useState } from "react";
import { API_BASE, type BusEvent } from "../lib/api";

export type LiveTagEvent = {
  uid: string;
  device_id: string;
  planogram_id: string;
  firmware_version: string;
  tag_id: string;
  count: number;
  avg_confidence: number;
  ingested_at: string;
};

/**
 * Subscribes to GET /stream (Server-Sent Events) and exposes a rolling
 * window of recent tag events for the live feed. The cap keeps the DOM
 * cheap even when traffic spikes.
 */
export function useLiveStream(tenantKey: string | null, max = 50) {
  const [events, setEvents] = useState<LiveTagEvent[]>([]);
  const [connected, setConnected] = useState(false);
  const counterRef = useRef(0);

  useEffect(() => {
    if (!tenantKey) {
      setEvents([]);
      setConnected(false);
      return;
    }

    const url = new URL("/stream", API_BASE);
    url.searchParams.set("tenant_key", tenantKey);
    const es = new EventSource(url.toString());

    es.addEventListener("ready", () => setConnected(true));

    es.addEventListener("aggregate.ingested", (raw) => {
      try {
        const data: BusEvent = JSON.parse((raw as MessageEvent).data);
        const ts = data.ingested_at;
        const next: LiveTagEvent[] = data.payload.tags.map((t) => ({
          uid: `${++counterRef.current}-${t.tag_id}`,
          device_id: data.payload.device_id,
          planogram_id: data.payload.planogram_id,
          firmware_version: data.payload.firmware_version,
          tag_id: t.tag_id,
          count: t.count,
          avg_confidence: t.avg_confidence,
          ingested_at: ts,
        }));
        setEvents((prev) => [...next, ...prev].slice(0, max));
      } catch {
        /* swallow malformed events */
      }
    });

    es.onerror = () => setConnected(false);

    return () => {
      es.close();
      setConnected(false);
    };
  }, [tenantKey, max]);

  return { events, connected };
}
